"use client";

import { useState } from "react";
import type { KundaliRequest } from "@ishvaram/api-client";
import BirthDetailsForm from "@/components/BirthDetailsForm";
import { NAKSHATRAS } from "./types";
import Button from "@ishvaram/ui/Button";

interface BabyNamesFormProps {
  loading: boolean;
  error?: string | null;
  onBirthSubmit: (data: KundaliRequest) => void;
  onNakshatraSubmit: (data: { nakshatra: string; pada?: number; birthDate: string }) => void;
  genderFilter: string;
  onGenderFilterChange: (gender: string) => void;
}

export function BabyNamesForm({
  loading,
  error,
  onBirthSubmit,
  onNakshatraSubmit,
  genderFilter,
  onGenderFilterChange,
}: BabyNamesFormProps) {
  const [mode, setMode] = useState<"birth" | "nakshatra">("birth");
  const [selectedNakshatra, setSelectedNakshatra] = useState("");
  const [selectedPada, setSelectedPada] = useState<string>("");
  const [manualBirthDate, setManualBirthDate] = useState("");

  const handleNakshatraSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onNakshatraSubmit({
      nakshatra: selectedNakshatra,
      pada: selectedPada ? Number(selectedPada) : undefined,
      birthDate: manualBirthDate,
    });
  };

  return (
    <div className="mx-auto max-w-md">
      {/* Mode Toggle */}
      <div className="flex rounded-lg overflow-hidden mb-6 border-[1.5px] border-ish-border">
        <button
          className={`flex-1 py-2.5 text-[13px] font-semibold transition-all ${
            mode === "birth" ? "bg-ish-accent text-white" : "bg-white text-ish-text-muted"
          }`}
          onClick={() => setMode("birth")}
        >
          By Birth Details
        </button>
        <button
          className={`flex-1 py-2.5 text-[13px] font-semibold transition-all ${
            mode === "nakshatra" ? "bg-ish-accent text-white" : "bg-white text-ish-text-muted"
          }`}
          onClick={() => setMode("nakshatra")}
        >
          Select Nakshatra
        </button>
      </div>

      {/* Gender Filter */}
      <div className="mb-4">
        <label className="block text-[13px] font-semibold mb-2 text-ish-dark">Baby Gender</label>
        <div className="flex gap-2">
          {[
            { value: "all", label: "All" },
            { value: "male", label: "Boy" },
            { value: "female", label: "Girl" },
          ].map((opt) => (
            <button
              key={opt.value}
              className={`flex-1 py-2 rounded-lg text-[13px] font-semibold transition-all border-[1.5px] ${
                genderFilter === opt.value
                  ? "bg-ish-accent text-white border-ish-accent"
                  : "bg-white text-ish-text-muted border-ish-border"
              }`}
              onClick={() => onGenderFilterChange(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {mode === "birth" ? (
        <BirthDetailsForm
          onSubmit={onBirthSubmit}
          loading={loading}
          submitLabel="Find Baby Names"
          error={error ?? undefined}
          analyticsToolName="baby-names"
        />
      ) : (
        <form onSubmit={handleNakshatraSubmit} className="card-sacred p-6 sm:p-8 space-y-5">
          {/* Nakshatra Dropdown */}
          <div>
            <label className="block text-[13px] font-semibold mb-2 text-ish-dark">
              Select Nakshatra
            </label>
            <select
              required
              value={selectedNakshatra}
              onChange={(e) => setSelectedNakshatra(e.target.value)}
              className="input-sacred w-full rounded-lg px-4 py-3 text-[14px] text-ish-dark"
            >
              <option value="">Choose nakshatra...</option>
              {NAKSHATRAS.map((n) => (
                <option key={n.id} value={n.id}>
                  {n.name}
                </option>
              ))}
            </select>
          </div>

          {/* Pada */}
          <div>
            <label className="block text-[13px] font-semibold mb-2 text-ish-dark">
              Pada <span className="font-normal text-ish-text-faint">(optional)</span>
            </label>
            <div className="flex gap-2">
              {["", "1", "2", "3", "4"].map((p) => (
                <button
                  key={p}
                  type="button"
                  className={`flex-1 py-2 rounded-lg text-[13px] font-semibold transition-all border-[1.5px] ${
                    selectedPada === p
                      ? "bg-ish-accent text-white border-ish-accent"
                      : "bg-white text-ish-text-muted border-ish-border"
                  }`}
                  onClick={() => setSelectedPada(p)}
                >
                  {p || "All"}
                </button>
              ))}
            </div>
          </div>

          {/* Birth Date (for numerology) */}
          <div>
            <label className="block text-[13px] font-semibold mb-2 text-ish-dark">
              Baby&apos;s Birth Date{" "}
              <span className="font-normal text-ish-text-faint">(for numerology scoring)</span>
            </label>
            <input
              type="date"
              required
              min="1900-01-01"
              max={new Date().toISOString().slice(0, 10)}
              value={manualBirthDate}
              onChange={(e) => setManualBirthDate(e.target.value)}
              className="input-sacred w-full rounded-lg px-4 py-3 text-[14px] text-ish-dark"
            />
          </div>

          {/* Error */}
          {error && (
            <div className="text-[13px] rounded-lg p-3.5 text-color-error-text bg-color-error-bg border border-color-error-tint">
              {error}
            </div>
          )}

          {/* Submit */}
          <Button
            type="submit"
            onPress={() => {}}
            loading={loading}
            title="Find Baby Names →"
            className="w-full font-semibold py-3.5 rounded-lg text-[14px]"
          />
        </form>
      )}
    </div>
  );
}
